import React, { useState, useEffect, useCallback } from 'react';
import axios from '../utils/api';
import { TemplateArea, ContentComponent, HtmlTemplate, TemplateColumn } from '../types/template';
import './TemplateAreaManager.css';

interface TemplateAreaManagerProps {
  templateId: number;
  onAreasChange: (areas: TemplateArea[]) => void;
  onCancel: () => void;
}

const TemplateAreaManager: React.FC<TemplateAreaManagerProps> = ({ 
  templateId, 
  onAreasChange, 
  onCancel 
}) => {
  const [areas, setAreas] = useState<TemplateArea[]>([]);
  const [availableComponents, setAvailableComponents] = useState<ContentComponent[]>([]);
  const [template, setTemplate] = useState<HtmlTemplate | null>(null);
  const [columns, setColumns] = useState<TemplateColumn[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [draggedArea, setDraggedArea] = useState<TemplateArea | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<number | null>(null);

  // Стандартные типы областей
  const standardAreaTypes = [
    { type: 'header', name: 'Хедер (Шапка сайта)', description: 'Область для размещения компонентов типа "хедер"' },
    { type: 'navigation', name: 'Навигация', description: 'Область для размещения компонентов типа "навигация"' },
    { type: 'dynamic', name: 'Динамический контент', description: 'Область для размещения динамических компонентов при генерации. Компоненты выбираются пользователем или автоматически системой в соответствии с правилами совместимости.' }
  ];

  // Состояния для создания нового контейнера
  const [showCreateContainer, setShowCreateContainer] = useState(false);
  const [newContainerName, setNewContainerName] = useState('');
  const [newContainerDescription, setNewContainerDescription] = useState('');
  const [newContainerType, setNewContainerType] = useState<'dynamic' | 'custom'>('custom');

  const fetchTemplateData = useCallback(async () => {
    try {
      const response = await axios.get(`/api/admin/html-templates/${templateId}`);
      setTemplate(response.data);
      setColumns(response.data.columns || []);
    } catch (err) {
      console.error('Ошибка загрузки данных шаблона:', err);
      setError('Не удалось загрузить данные шаблона');
    }
  }, [templateId]);

  const fetchTemplateAreas = useCallback(async () => {
    try {
      const response = await axios.get(`/api/admin/template-areas?template_id=${templateId}`);
      setAreas(response.data);
    } catch (err) {
      console.error('Ошибка загрузки областей шаблона:', err);
      setError('Не удалось загрузить области шаблона');
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  const fetchAvailableComponents = useCallback(async () => {
    try {
      const response = await axios.get('/api/admin/template-components');
      setAvailableComponents(response.data);
    } catch (err) {
      console.error('Ошибка загрузки компонентов:', err);
    }
  }, []);

  useEffect(() => {
    fetchTemplateData();
    fetchTemplateAreas();
    fetchAvailableComponents();
  }, [fetchAvailableComponents, fetchTemplateAreas, fetchTemplateData]);

  const addArea = (areaType: string) => {
    const newArea: TemplateArea = {
      template_id: templateId,
      name: standardAreaTypes.find(t => t.type === areaType)?.name || areaType,
      description: standardAreaTypes.find(t => t.type === areaType)?.description || '',
      area_type: areaType,
      is_active: true,
      width: '100%',
      height: 'auto'
    };

    setAreas(prev => [...prev, newArea]);
  };

  const updateArea = (areaId: number, updates: Partial<TemplateArea>) => {
    setAreas(prev => prev.map(area => 
      area.id === areaId ? { ...area, ...updates } : area
    ));
  };

  const removeArea = (areaId: number) => {
    setAreas(prev => prev.filter(area => area.id !== areaId));
  };

  const saveAreas = async () => {
    try {
      setLoading(true);
      
      // Сохраняем все области
      for (const area of areas) {
        if (area.id) {
          // Обновляем существующую область
          await axios.put(`/api/admin/template-areas/${area.id}`, area);
        } else {
          // Создаем новую область
          const response = await axios.post('/api/admin/template-areas', area);
          area.id = response.data.id;
        }
      }

      onAreasChange(areas);
    } catch (err) {
      console.error('Ошибка сохранения областей:', err);
      setError('Не удалось сохранить области шаблона');
    } finally {
      setLoading(false);
    }
  };

  const getAvailableAreaTypes = () => {
    const usedTypes = areas.map(area => area.area_type);
    return standardAreaTypes.filter(type => !usedTypes.includes(type.type));
  };

  // Функция создания нового HTML-контейнера
  const handleCreateContainer = async () => {
    if (!newContainerName.trim()) {
      setError('Название контейнера не может быть пустым');
      return;
    }

    try {
      const response = await axios.post('/api/admin/template-areas', {
        template_id: templateId,
        name: newContainerName,
        description: newContainerDescription || `Пользовательский контейнер: ${newContainerName}`,
        area_type: newContainerType,
        width: '100%',
        height: 'auto'
      });

      const newArea = response.data;
      setAreas([...areas, newArea]);
      // Очищаем форму
      setNewContainerName('');
      setNewContainerDescription('');
      setNewContainerType('custom');
      setShowCreateContainer(false);
    } catch (error: any) {
      console.error('Error creating container:', error);
      setError(error.response?.data?.error || 'Ошибка создания контейнера');
    }
  };

  // Функции для drag-and-drop
  const handleDragStart = (e: React.DragEvent, area: TemplateArea) => {
    console.log(`🎯 Начало перетаскивания области: ${area.name}`);
    setDraggedArea(area);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', area.name); // Для совместимости
    
    // Добавляем класс для визуального feedback
    const dragElement = e.currentTarget as HTMLElement;
    dragElement.classList.add('dragging');
  };

  const handleDragOver = (e: React.DragEvent, columnIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    
    // Устанавливаем drag over только если это действительно новая колонка
    if (dragOverColumn !== columnIndex) {
      console.log(`📍 Перетаскивание над колонкой: ${columns[columnIndex]?.column_name}`);
      setDragOverColumn(columnIndex);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Проверяем, что мы действительно покинули колонку
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      console.log('🚪 Покинули область перетаскивания');
      setDragOverColumn(null);
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    // Очищаем визуальные эффекты после завершения перетаскивания
    const dragElement = e.currentTarget as HTMLElement;
    dragElement.classList.remove('dragging');
    setDragOverColumn(null);
    setDraggedArea(null);
    console.log('🏁 Завершение перетаскивания');
  };

  const handleDrop = (e: React.DragEvent, columnIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log(`🎯 Drop в колонке ${columnIndex}, draggedArea:`, draggedArea);
    
    if (draggedArea && columns[columnIndex]) {
      const column = columns[columnIndex];
      console.log(`✅ Размещаем "${draggedArea.name}" в колонке "${column.column_name}"`);
      
      // Обновляем описание области, чтобы показать привязку к колонке
      const baseDescription = draggedArea.description?.replace(/\s*\(размещен[аео] в колонке:.*?\)/, '') || '';
      const newDescription = `${baseDescription} (размещено в колонке: ${column.column_name})`.trim();
      
      // Обновляем область в локальном состоянии
      const updatedAreas = areas.map(area => 
        area.id === draggedArea.id 
          ? { ...area, description: newDescription }
          : area
      );
      setAreas(updatedAreas);
      
      // Также обновляем в базе данных
      updateArea(draggedArea.id || 0, { 
        description: newDescription
      });
      
      // Показываем уведомление пользователю
      console.log(`🎉 HTML-контейнер "${draggedArea.name}" успешно размещен в колонке "${column.column_name}"`);
      
      // Временно показываем уведомление (можно заменить на toast)
      setError(`✅ Контейнер "${draggedArea.name}" размещен в колонке "${column.column_name}"`);
      setTimeout(() => setError(''), 3000);
    } else {
      console.log('❌ Ошибка: нет draggedArea или колонки');
    }
    
    // Очищаем состояние
    setDragOverColumn(null);
    setDraggedArea(null);
  };

  const getComponentsForAreaType = (areaType: string) => {
    return availableComponents.filter(component => 
      component.template_area_type === areaType
    );
  };

  if (loading) {
    return <div className="template-area-manager loading">Загрузка...</div>;
  }

  return (
    <div className="template-area-manager">
      <div className="template-area-manager__header">
        <h3>Управление областями шаблона</h3>
        {template && (
          <div className="template-area-manager__template-info">
            <h4>{template.name}</h4>
            <p>Тип макета: <strong>{template.layout_type}</strong> | Колонок: <strong>{template.column_count}</strong></p>
          </div>
        )}
        <p className="template-area-manager__description">
          Создайте области для типов компонентов. При генерации сайта система автоматически 
          выберет конкретные компоненты для каждой области.
        </p>
      </div>

      {/* Отображение колонок шаблона */}
      {columns.length > 0 && (
        <div className="template-area-manager__columns">
          <h4>Структура колонок шаблона</h4>
          <div className="template-area-manager__columns-preview">
            {columns.map((column, index) => (
              <div 
                key={column.id || index} 
                className={`template-area-manager__column ${
                  dragOverColumn === index ? 'template-area-manager__column--drag-over' : ''
                }`}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
              >
                <div className="template-area-manager__column-header">
                  <span className="template-area-manager__column-name">{column.column_name}</span>
                  <span className="template-area-manager__column-width">{column.width_value}</span>
                </div>
                <div className="template-area-manager__column-content">
                  {/* Показываем области, привязанные к этой колонке */}
                  {areas
                    .filter(area => area.description?.includes(`размещено в колонке: ${column.column_name}`))
                    .map((area, areaIndex) => (
                      <div key={area.id || areaIndex} className="template-area-manager__column-area">
                        <span className="template-area-manager__column-area-name">{area.name}</span>
                        <span className="template-area-manager__column-area-type">{area.area_type}</span>
                      </div>
                    ))
                  }
                  <div className="template-area-manager__column-dropzone">
                    <span>📦 Перетащите области сюда</span>
                    <small>Колонка: {column.column_name}</small>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="template-area-manager__error">
          {error}
        </div>
      )}

      <div className="template-area-manager__areas">
        <div className="template-area-manager__areas-header">
          <h4>HTML-контейнеры шаблона</h4>
          <button 
            type="button"
            className="template-area-manager__create-button"
            onClick={() => setShowCreateContainer(true)}
          >
            + Создать контейнер
          </button>
        </div>
        
        {/* Форма создания нового контейнера */}
        {showCreateContainer && (
          <div className="template-area-manager__create-form">
            <div className="template-area-manager__create-form-header">
              <h5>Создание нового HTML-контейнера</h5>
              <button 
                type="button"
                className="template-area-manager__close-button"
                onClick={() => {
                  setShowCreateContainer(false);
                  setNewContainerName('');
                  setNewContainerDescription('');
                  setNewContainerType('custom');
                }}
              >
                ✕
              </button>
            </div>
            
            <div className="template-area-manager__create-form-body">
              <div className="template-area-manager__area-field">
                <label>Название контейнера*:</label>
                <input
                  type="text"
                  value={newContainerName}
                  onChange={(e) => setNewContainerName(e.target.value)}
                  placeholder="Например: Баннерный блок, Форма заявки, Новостная лента"
                />
              </div>
              
              <div className="template-area-manager__area-field">
                <label>Описание:</label>
                <textarea
                  value={newContainerDescription}
                  onChange={(e) => setNewContainerDescription(e.target.value)}
                  placeholder="Опишите назначение этого контейнера"
                  rows={3}
                />
              </div>
              
              <div className="template-area-manager__area-field">
                <label>Тип контейнера:</label>
                <select
                  value={newContainerType}
                  onChange={(e) => setNewContainerType(e.target.value as 'dynamic' | 'custom')}
                >
                  <option value="custom">Пользовательский контейнер</option>
                  <option value="dynamic">Динамический контейнер</option>
                </select>
              </div>
              
              <div className="template-area-manager__create-form-actions">
                <button 
                  type="button"
                  className="template-area-manager__save-button"
                  onClick={handleCreateContainer}
                  disabled={!newContainerName.trim()}
                >
                  Создать контейнер
                </button>
                <button 
                  type="button"
                  className="template-area-manager__cancel-button"
                  onClick={() => {
                    setShowCreateContainer(false);
                    setNewContainerName('');
                    setNewContainerDescription('');
                    setNewContainerType('custom');
                  }}
                >
                  Отмена
                </button>
              </div>
            </div>
          </div>
        )}
        
        {areas.length === 0 ? (
          <div className="template-area-manager__empty">
            <p>Области не созданы. Создайте области для типов компонентов.</p>
          </div>
        ) : (
          <div className="template-area-manager__areas-list">
            {areas.map((area, index) => (
              <div 
                key={area.id || index} 
                className="template-area-manager__area"
              >
                <div 
                  className="template-area-manager__area-header"
                  draggable
                  onDragStart={(e) => handleDragStart(e, area)}
                  onDragEnd={handleDragEnd}
                >
                  <div className="template-area-manager__area-drag-handle">
                    ⋮⋮ {/* Drag handle */}
                  </div>
                  <h5>{area.name}</h5>
                  <span className={`template-area-manager__area-type template-area-manager__area-type--${area.area_type}`}>
                    {area.area_type}
                  </span>
                </div>
                
                <div className="template-area-manager__area-content">
                  <div className="template-area-manager__area-field">
                    <label>Название:</label>
                    <input
                      type="text"
                      value={area.name}
                      onChange={(e) => updateArea(area.id || index, { name: e.target.value })}
                      placeholder="Название области"
                    />
                  </div>
                  
                  <div className="template-area-manager__area-field">
                    <label>Описание:</label>
                    <textarea
                      value={area.description || ''}
                      onChange={(e) => updateArea(area.id || index, { description: e.target.value })}
                      placeholder="Описание области"
                    />
                  </div>
                  
                  <div className="template-area-manager__area-field">
                    <label>Ширина:</label>
                    <input
                      type="text"
                      value={area.width || '100%'}
                      onChange={(e) => updateArea(area.id || index, { width: e.target.value })}
                      placeholder="100%"
                    />
                  </div>
                  
                  <div className="template-area-manager__area-field">
                    <label>Высота:</label>
                    <input
                      type="text"
                      value={area.height || 'auto'}
                      onChange={(e) => updateArea(area.id || index, { height: e.target.value })}
                      placeholder="auto"
                    />
                  </div>
                  
                  <div className="template-area-manager__area-field">
                    <label>
                      <input
                        type="checkbox"
                        checked={area.is_active}
                        onChange={(e) => updateArea(area.id || index, { is_active: e.target.checked })}
                      />
                      Активна
                    </label>
                  </div>

                  {/* Отображение доступных компонентов для этой области */}
                  <div className="template-area-manager__area-components">
                    <h6>Доступные компоненты ({area.area_type}):</h6>
                    <div className="template-area-manager__components-list">
                      {getComponentsForAreaType(area.area_type).length > 0 ? (
                        getComponentsForAreaType(area.area_type).map((component, compIndex) => (
                          <div key={component.id || compIndex} className="template-area-manager__component">
                            <span className="template-area-manager__component-name">
                              {component.component_name}
                            </span>
                            <span className="template-area-manager__component-variant">
                              {component.variant_code}
                            </span>
                          </div>
                        ))
                      ) : (
                        <p className="template-area-manager__no-components">
                          Нет доступных компонентов для типа "{area.area_type}"
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="template-area-manager__area-actions">
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => removeArea(area.id || index)}
                  >
                    Удалить
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="template-area-manager__add-area">
        <h4>Добавить стандартные области</h4>
        <div className="template-area-manager__add-area-options">
          {getAvailableAreaTypes().map(type => (
            <button
              key={type.type}
              type="button"
              className="btn btn-secondary"
              onClick={() => addArea(type.type)}
            >
              {type.name}
            </button>
          ))}
        </div>
        
        {getAvailableAreaTypes().length === 0 && (
          <p className="template-area-manager__no-more-areas">
            Все стандартные области уже добавлены. 
            Используйте кнопку "Создать контейнер" для добавления пользовательских HTML-контейнеров.
          </p>
        )}
      </div>

      <div className="template-area-manager__actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={saveAreas}
          disabled={loading}
        >
          {loading ? 'Сохранение...' : 'Сохранить области'}
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
        >
          Отмена
        </button>
      </div>
    </div>
  );
};

export default TemplateAreaManager;

